import { IQuery } from '@introspection/index';
import { Injectable, Injector } from '@rxdi/core';
import { ApolloClient, importQuery } from '@rxdi/graphql-client';

/**
 * Page-load data in ONE request (same idea as Lambforge's query bootstrap).
 *
 * `homeBootstrap` fetches what the shell, home page and footer need, then each
 * part is written into the Apollo cache under the SAME document + variables
 * its owning service queries with - so those (cache-first) queries resolve
 * from the cache and the components need no changes. One GET = one URL for
 * the edge cache.
 *
 * Adding a part: the document and variables must match the owning service's
 * call exactly, and that call must be cache-first.
 */
const PARTS: { document: string; field: keyof IQuery; variables?: Record<string, unknown> }[] = [
  { document: 'site-content.query.graphql', field: 'siteContent' },
  // BlogService.listPosts() with no arguments (home journal)
  { document: 'list-posts.query.graphql', field: 'listPosts', variables: {} },
  { document: 'list-categories.query.graphql', field: 'listCategories' },
];

/** Queries never wait longer than this for the bootstrap. */
const MAX_WAIT_MS = 8000;

@Injectable()
export class QueryBootstrapService {
  @Injector(ApolloClient) private graphql: ApolloClient;

  private done: Promise<void> | null = null;

  /** Starts once; later calls return the same promise. Never rejects. */
  start(): Promise<void> {
    this.done ??= this.run();
    return this.done;
  }

  /** Resolves when the bootstrap finished, failed, timed out - or was never started. */
  ready(): Promise<void> {
    if (!this.done) return Promise.resolve();
    return Promise.race([this.done, new Promise<void>((resolve) => setTimeout(resolve, MAX_WAIT_MS))]);
  }

  private async run(): Promise<void> {
    try {
      const { data } = await this.graphql.query<IQuery>({
        query: importQuery('home-bootstrap.query.graphql'),
        fetchPolicy: 'no-cache',
      });
      for (const part of PARTS) {
        if (data?.[part.field] === undefined) continue;
        this.graphql.writeQuery({
          query: importQuery(part.document),
          variables: part.variables,
          data: { [part.field]: data[part.field] },
        });
      }
    } catch (e) {
      // the components then load their own data, as before
      console.warn('[QueryBootstrap] failed, falling back to per-component queries', e);
    }
  }
}
